// Plan-gate helper for the SuperAdmin plan catalogue.
//
// `sidebarModuleAccess` is written to localStorage at login from the server's
// `getEnabledSidebarModules` (controller/roles.js). It contains the slugs the
// company's plan has ENABLED, plus a `__plan_configured__` sentinel whenever a
// plan exists at all — the sentinel is what lets an empty-but-configured plan
// be told apart from "no plan configured", which would otherwise both look like
// an empty list and skip the gate entirely.
//
// Enforcement order for a plan-gated feature is: plan gate FIRST, then the RBAC
// `getResource` check. A module the company was never sold must not appear even
// for a user whose role would otherwise permit it.
//
// The same logic already lives inline in `hooks/useRoleMenu.js` and
// `SidebarContent.js`; new call sites should use this instead of adding a
// fourth copy, and those two can adopt it when they are next touched.

const PLAN_CONFIGURED_SENTINEL = "__plan_configured__";

/**
 * The enabled plan slugs for the logged-in company.
 *
 * @returns {Set<string>} empty when nothing is stored or the value is unusable
 */
export const getPlanModuleAccess = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("sidebarModuleAccess") || "[]");
    return new Set(Array.isArray(raw) ? raw : []);
  } catch (error) {
    return new Set();
  }
};

/**
 * Is this plan module enabled for the logged-in company?
 *
 * Mirrors the server's own gate, including the "no plan configured at all"
 * escape: when no plan exists the set is empty and access falls through to RBAC
 * alone, matching `useRoleMenu.js`'s `isPlanEnabled`. A company WITH a plan
 * always carries the sentinel, so an absent slug there is a real denial.
 *
 * @param {string|string[]} slugs - slug, or slugs where any one enabled suffices
 * @returns {boolean}
 */
export const isPlanModuleEnabled = (slugs) => {
  const wanted = (Array.isArray(slugs) ? slugs : [slugs]).filter(Boolean);
  if (wanted.length === 0) return true;
  const access = getPlanModuleAccess();
  if (access.size === 0) return true;
  return wanted.some((slug) => access.has(slug));
};

/** Plan catalogue slugs for the Shift Swap / Double Shift / Shift Change module. */
export const SHIFT_MODULE_PLAN_SLUGS = ["shift_swap_request", "shift_change_request", "double_shift_request"];

/** Plan catalogue slug for the Flexi Leave module. */
export const FLEXI_LEAVE_PLAN_SLUGS = ['flexi_leave'];

/**
 * Is ATS tracking full flow enabled company-wide?
 * Checks authUser.companyId.enableAtsFullFlow, localStorage user_data, and localStorage enableAtsFullFlow.
 *
 * @param {object} [authUser] - authUser from Redux auth state
 * @returns {boolean}
 */
export const isAtsCompanyEnabled = (authUser) => {
  if (authUser?.companyId && typeof authUser.companyId === "object" && typeof authUser.companyId.enableAtsFullFlow === "boolean") {
    return authUser.companyId.enableAtsFullFlow;
  }
  if (typeof localStorage === "undefined") {
    return false;
  }
  try {
    const rawUserData = localStorage.getItem("user_data");
    if (rawUserData) {
      const parsed = JSON.parse(rawUserData);
      if (parsed?.companyId && typeof parsed.companyId === "object" && typeof parsed.companyId.enableAtsFullFlow === "boolean") {
        return parsed.companyId.enableAtsFullFlow;
      }
    }
  } catch (e) {}
  try {
    const rawToggle = localStorage.getItem("enableAtsFullFlow");
    if (rawToggle !== null) {
      return rawToggle === "true";
    }
  } catch (e) {}
  return false;
};

export { PLAN_CONFIGURED_SENTINEL };
